import { useCallback } from 'react'

import { useActiveBookkeepingBankAccount } from '@features/accounts/hooks/useActiveBookkeepingBankAccount'
import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { KYC_REGISTRATION_COMPLETED_STATUS } from '@features/eInvoicing/constants'
import { useOpenBankingAccounts } from '@features/openBanking/hooks/useOpenBankingAccounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { Scope } from '../../../enums/scope'
import { useEInvoicingIntegration } from '../../../hooks/useEInvoicingIntegration'
import { useIsBasicPlan } from '../../../hooks/useIsBasicPlan'
import { isKontistAccount } from '../../../utils/accountTypeCheckers'
import { isAuthorized } from '../../../utils/isAuthorized'
import { widgetId } from '../constants/widgetId'
import { Widget } from '../types/widget'
import {
  getActiveOpenBankingAccounts,
  getUnhealthyOpenBankingAccounts,
} from '../widgets/BankIntegration/utils/getUnhealthyOpenBankingAccounts'
import { useFindAccountantAvailability } from './useFindAccountantAvailability'

export const useFilterWidgetsByBusinessRules = () => {
  const { isFindAccountAvailable } = useFindAccountantAvailability()
  const { isBasicPlan } = useIsBasicPlan()
  const { organization } = useUserOrganization()
  const { bookkeepingBankAccounts } = useBookkeepingBankAccounts()
  const { activeBookkeepingBankAccount } = useActiveBookkeepingBankAccount()
  const { isEInvoicingEnabled, kycStatus } = useEInvoicingIntegration(organization)
  const { openBankingAccounts, isLoading: isLoadingOpenBankingAccounts } = useOpenBankingAccounts()

  const activeBookeepingBankAccounts = bookkeepingBankAccounts?.filter((account) => !account.isArchived) ?? []
  const hasOnlyOneKontistAccount =
    activeBookeepingBankAccounts.length === 1 && isKontistAccount(activeBookeepingBankAccounts[0])
  // Cegid clients get their own bank widget (connect / needs-attention), driven by open-banking connections.
  // It is hidden here (not inside the widget) so it doesn't leave an empty slot in the dashboard grid.
  const hasConnectedAccounts = getActiveOpenBankingAccounts(openBankingAccounts).length > 0
  const hasUnhealthyAccounts = getUnhealthyOpenBankingAccounts(openBankingAccounts).length > 0
  const shouldHideCpaBankIntegration =
    !isLoadingOpenBankingAccounts && (hasConnectedAccounts ? !hasUnhealthyAccounts : !isAuthorized(Scope.BankSyncWrite))
  const shouldHideStandardBankIntegration = isBasicPlan || !activeBookkeepingBankAccount || hasOnlyOneKontistAccount
  const shouldHideBankIntegration = organization?.isCegid
    ? shouldHideCpaBankIntegration
    : shouldHideStandardBankIntegration

  const shouldHideCegidEInvoices =
    KYC_REGISTRATION_COMPLETED_STATUS.includes(kycStatus) || !isEInvoicingEnabled || !organization?.isCegid

  const filterWidgetsByBusinessRules = useCallback(
    <T extends Widget>(widgets: T[]): T[] => {
      return widgets.filter(
        ({ id }) =>
          ![
            [widgetId.FindAccountant, !isFindAccountAvailable],
            [widgetId.BankIntegration, shouldHideBankIntegration],
            [widgetId.Vat, organization?.isVatExempted],
            [widgetId.CegidEInvoices, shouldHideCegidEInvoices],
          ].some(([widgetId, condition]) => id === widgetId && condition),
      )
    },
    [isFindAccountAvailable, organization?.isVatExempted, shouldHideBankIntegration, shouldHideCegidEInvoices],
  )

  return { filterWidgetsByBusinessRules }
}
